/**
 * InventoryGrid - Full-screen grid inventory with drag-drop support and configurable rows/columns.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';
import type { InventoryItem, ItemRenderer, SlotClearer } from './InventoryBar.js';
import type * as ENGINE from '../../index.js';

export interface InventoryGridOptions<T extends InventoryItem = InventoryItem> extends BaseUIComponentOptions {
  /** Number of columns in the grid */
  columns?: number;
  /** Number of rows in the grid */
  rows?: number;
  /** Size of each slot in pixels */
  slotSize?: number;
  /** Gap between slots in pixels */
  slotGap?: number;
  /** Custom item renderer function */
  itemRenderer?: ItemRenderer<T>;
  /** Custom slot clearer function */
  slotClearer?: SlotClearer;
  /** CSS class for filled slots */
  filledClass?: string;
  /** CSS class for empty slots */
  emptyClass?: string;
  /** CSS class for drag hover state */
  dragHoverClass?: string;
  /** CSS selector for item content within slot */
  itemContentSelector?: string;
  /** Enable drag and drop functionality */
  enableDragDrop?: boolean;
  /** Callback function called when grid is closed */
  onClose?: () => void;
}

export class InventoryGrid<T extends InventoryItem = InventoryItem> extends BaseUIComponent<InventoryGridOptions<T>> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Inventory Grid',
    category: 'inventory',
    summary: 'Full-screen grid inventory with drag-drop and configurable rows / columns.',
    useCases: ['inventory', 'backpack', 'storage', 'chest', 'loot grid'],
    optionsType: 'InventoryGridOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/inventory-grid.html',
      styles: '@engine/assets/ui/styles/inventory-grid.css',
    },
  };

  private items: (T | null)[] = [];
  private slotElements: ENGINE.UIElement[] = [];
  private draggedItem: T | null = null;
  private draggedFromIndex: number = -1;
  private dragImageCanvas: HTMLCanvasElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: '@engine/assets/ui/templates/inventory-grid.html',
      stylesPath: '@engine/assets/ui/styles/inventory-grid.css'
    };
  }

  protected override getDefaultOptions(): Required<InventoryGridOptions<T>> {
    return {
      position: 'center',
      visible: false,
      customClasses: [],
      customStyles: {},
      columns: 6,
      rows: 4,
      slotSize: 64,
      slotGap: 8,
      itemRenderer: this.defaultItemRenderer.bind(this),
      slotClearer: this.defaultSlotClearer.bind(this),
      filledClass: 'filled',
      emptyClass: 'empty',
      dragHoverClass: 'drag-hover',
      itemContentSelector: '.item-content',
      enableDragDrop: true,
      onClose: () => {}
    };
  }

  protected override getInitialData() {
    const totalSlots = this.options.columns * this.options.rows;
    const slots = Array.from({ length: totalSlots }, (_, index) => ({
      index,
      row: Math.floor(index / this.options.columns),
      col: index % this.options.columns,
      isEmpty: true
    }));

    // Generate slots HTML
    const slotsHTML = slots.map(slot => `
      <div class="inventory-slot empty" data-slot="${slot.index}" data-row="${slot.row}" data-col="${slot.col}" style="width: ${this.options.slotSize}px; height: ${this.options.slotSize}px;">
        <div class="item-content"></div>
        <div class="slot-border"></div>
      </div>
    `).join('');

    // Calculate grid dimensions with reasonable constraints
    const gridWidth = Math.min(
      (this.options.slotSize + this.options.slotGap) * this.options.columns - this.options.slotGap,
      window.innerWidth - 100 // Leave 100px margin
    );
    const gridHeight = Math.min(
      (this.options.slotSize + this.options.slotGap) * this.options.rows - this.options.slotGap + 80, // +80 for header
      window.innerHeight - 100 // Leave 100px margin
    );

    return {
      slots,
      columns: this.options.columns,
      rows: this.options.rows,
      slotSize: this.options.slotSize,
      slotGap: this.options.slotGap,
      gridWidth,
      gridHeight,
      slotsHTML
    };
  }

  protected override cacheElements(): void {
    const totalSlots = this.options.columns * this.options.rows;

    for (let i = 0; i < totalSlots; i++) {
      const slotElement = this.layout?.querySelector(`.inventory-slot[data-slot="${i}"]`);
      if (slotElement) {
        const wrappedElement = this.createElementWrapper(slotElement);
        this.elements.set(`slot-${i}`, wrappedElement);
        this.slotElements.push(wrappedElement);
      }
    }

    // Initialize items array with nulls
    this.items = new Array(totalSlots).fill(null);
  }

  protected override async onInitialize(): Promise<void> {
    await super.onInitialize();

    if (this.options.enableDragDrop) {
      this.setupDragAndDrop();
    }

    // Setup close button event handler
    this.setupCloseButton();
  }

  /**
   * Default item renderer - displays item as text
   */
  private defaultItemRenderer(item: T, slotElement: HTMLElement): void {
    const contentElement = slotElement.querySelector(this.options.itemContentSelector) as HTMLElement;
    if (contentElement) {
      contentElement.textContent = item.id;
      contentElement.style.display = 'block';
      contentElement.title = `Item: ${item.id}`;
    }
  }

  /**
   * Default slot clearer - hides content
   */
  private defaultSlotClearer(slotElement: HTMLElement): void {
    const contentElement = slotElement.querySelector(this.options.itemContentSelector) as HTMLElement;
    if (contentElement) {
      contentElement.style.display = 'none';
      contentElement.textContent = '';
      contentElement.title = '';
    }
  }

  /**
   * Setup drag and drop functionality
   */
  private setupDragAndDrop(): void {
    this.slotElements.forEach((slotElement, index) => {
      const element = slotElement.element;

      // Make slots droppable
      element.addEventListener('dragover', (e) => this.handleDragOver(e, index));
      element.addEventListener('dragenter', (e) => this.handleDragEnter(e, index));
      element.addEventListener('dragleave', (e) => this.handleDragLeave(e, index));
      element.addEventListener('drop', (e) => this.handleDrop(e, index));

      // Make item content draggable
      const contentElement = element.querySelector(this.options.itemContentSelector) as HTMLElement;
      if (contentElement) {
        contentElement.addEventListener('dragstart', (e) => this.handleDragStart(e, index));
        contentElement.addEventListener('dragend', (e) => this.handleDragEnd(e, index));
      }
    });
  }

  private handleDragStart(e: DragEvent, index: number): void {
    const item = this.items[index];
    if (!item) return;

    this.draggedItem = item;
    this.draggedFromIndex = index;

    // Create custom drag image from the item content
    const contentElement = e.target as HTMLElement;
    this.createDragImage(contentElement);

    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', item.id);

      // Set custom drag image if available
      if (this.dragImageCanvas) {
        e.dataTransfer.setDragImage(this.dragImageCanvas, 25, 25);
      }
    }

    // Add dragging class to original slot
    this.slotElements[index].addClass('dragging');
  }

  private handleDragEnd(e: DragEvent, index: number): void {
    // Clean up drag state
    this.slotElements.forEach(slot => {
      slot.removeClass('dragging');
      slot.removeClass(this.options.dragHoverClass);
    });

    this.draggedItem = null;
    this.draggedFromIndex = -1;

    if (this.dragImageCanvas) {
      this.dragImageCanvas = null;
    }
  }

  private handleDragOver(e: DragEvent, index: number): void {
    if (!this.draggedItem) return;

    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  }

  private handleDragEnter(e: DragEvent, index: number): void {
    if (!this.draggedItem || index === this.draggedFromIndex) return;

    e.preventDefault();
    this.slotElements[index].addClass(this.options.dragHoverClass);
  }

  private handleDragLeave(e: DragEvent, index: number): void {
    if (!this.draggedItem) return;

    // Only remove hover class if we're actually leaving the slot
    const rect = this.slotElements[index].element.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      this.slotElements[index].removeClass(this.options.dragHoverClass);
    }
  }

  private handleDrop(e: DragEvent, toIndex: number): void {
    if (!this.draggedItem || this.draggedFromIndex === -1) return;

    e.preventDefault();
    this.slotElements[toIndex].removeClass(this.options.dragHoverClass);

    // Don't allow dropping on the same slot
    if (toIndex === this.draggedFromIndex) return;

    // Move the item
    this.moveItem(this.draggedFromIndex, toIndex);
  }

  private createDragImage(contentElement: HTMLElement): void {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = contentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Copy the visual appearance of the item
    const computedStyle = window.getComputedStyle(contentElement);
    ctx.fillStyle = computedStyle.backgroundColor || 'transparent';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add text if any
    if (contentElement.textContent) {
      ctx.fillStyle = computedStyle.color || 'white';
      ctx.font = computedStyle.font || '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(contentElement.textContent, canvas.width / 2, canvas.height / 2);
    }

    this.dragImageCanvas = canvas;
  }

  /**
   * Move an item from one slot to another
   */
  public moveItem(fromIndex: number, toIndex: number): boolean {
    if (fromIndex < 0 || fromIndex >= this.items.length ||
        toIndex < 0 || toIndex >= this.items.length ||
        fromIndex === toIndex) {
      return false;
    }

    const fromItem = this.items[fromIndex];
    const toItem = this.items[toIndex];

    if (!fromItem) return false;

    // Swap items (or place in empty slot)
    this.items[fromIndex] = toItem;
    this.items[toIndex] = fromItem;

    this.updateDisplay();
    return true;
  }

  /**
   * Set items in the grid, filling from top-left
   */
  public setItems(items: T[]): void {
    const totalSlots = this.options.columns * this.options.rows;
    this.items = new Array(totalSlots).fill(null);

    // Fill slots with items up to capacity
    for (let i = 0; i < Math.min(items.length, totalSlots); i++) {
      this.items[i] = items[i];
    }

    this.updateDisplay();
  }

  /**
   * Add an item to the first available slot
   */
  public addItem(item: T): boolean {
    const emptyIndex = this.items.findIndex(slot => slot === null);
    if (emptyIndex === -1) {
      return false; // Grid is full
    }

    this.items[emptyIndex] = item;
    this.updateDisplay();
    return true;
  }

  /**
   * Remove an item from a specific slot
   */
  public removeItem(index: number): boolean {
    if (index < 0 || index >= this.items.length || !this.items[index]) {
      return false;
    }

    this.items[index] = null;
    this.updateDisplay();
    return true;
  }

  /**
   * Remove an item by ID
   */
  public removeItemById(id: string): boolean {
    const index = this.items.findIndex(item => item?.id === id);
    if (index >= 0) {
      return this.removeItem(index);
    }
    return false;
  }

  /**
   * Get all items in the grid (excluding null slots)
   */
  public getItems(): T[] {
    return this.items.filter((item): item is T => item !== null);
  }

  /**
   * Get all items including their positions
   */
  public getItemsWithPositions(): { item: T | null; index: number; row: number; col: number }[] {
    return this.items.map((item, index) => ({
      item,
      index,
      row: Math.floor(index / this.options.columns),
      col: index % this.options.columns
    }));
  }

  /**
   * Check if the grid is full
   */
  public isFull(): boolean {
    return !this.items.includes(null);
  }

  /**
   * Get number of items in the grid
   */
  public getItemCount(): number {
    return this.items.filter(item => item !== null).length;
  }

  /**
   * Clear all items from the grid
   */
  public clearItems(): void {
    this.items.fill(null);
    this.updateDisplay();
  }

  /**
   * Find an item by ID
   */
  public findItem(id: string): T | undefined {
    return this.items.find(item => item?.id === id) ?? undefined;
  }

  /**
   * Check if the grid contains an item with the given ID
   */
  public hasItem(id: string): boolean {
    return this.items.some(item => item?.id === id);
  }

  /**
   * Toggle visibility of the inventory grid
   */
  public override toggle(): void {
    if (this.getIsVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  private static readonly RARITY_CLASSES = [
    'rarity-common',
    'rarity-uncommon',
    'rarity-rare',
    'rarity-epic',
    'rarity-legendary',
  ] as const;

  private updateDisplay(): void {
    this.slotElements.forEach((slotElement, index) => {
      const item = this.items[index];
      const contentElement = slotElement.element.querySelector(this.options.itemContentSelector) as HTMLElement;

      // Clear any previously applied rarity class.
      for (const cls of InventoryGrid.RARITY_CLASSES) {
        slotElement.removeClass(cls);
      }

      if (item) {
        slotElement.removeClass(this.options.emptyClass);
        slotElement.addClass(this.options.filledClass);
        this.options.itemRenderer(item, slotElement.element);

        // Apply rarity class from item metadata when present.
        const rarity = typeof item.rarity === 'string' ? item.rarity.toLowerCase() : undefined;
        if (rarity && (InventoryGrid.RARITY_CLASSES as readonly string[]).includes(`rarity-${rarity}`)) {
          slotElement.addClass(`rarity-${rarity}`);
        }

        if (this.options.enableDragDrop && contentElement) {
          contentElement.draggable = true;
        }
      } else {
        slotElement.removeClass(this.options.filledClass);
        slotElement.addClass(this.options.emptyClass);
        this.options.slotClearer(slotElement.element);

        if (contentElement) {
          contentElement.draggable = false;
        }
      }
    });
  }

  /**
   * Setup close button event handler
   */
  private setupCloseButton(): void {
    const closeButton = this.layout?.querySelector('.close-button') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.hide();
        this.options.onClose();
      });
    }
  }
}